// Updated CMComposer component with enhanced mention handling

import React, { useRef, useState, useMemo, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import './CMComposer.scss';
import { IconAttachment, IconEmoji, IconArrowUp, IconDirUp, IconMention } from 'components/Icons';
import EmojiPicker, { EmojiClickData } from 'emoji-picker-react';
import { useClickAway } from 'react-use';
import classNames from 'classnames';
import { useParams } from 'react-router-dom';
import { useCreateQueueCommentMutation, useReopenQueueRequestMutation } from 'hooks/queue';
import {
  AskRequest,
  AskRequestCreateType,
  AskRequestCreateTypes,
  AskRequestStatuses,
  HoneAbilityActions,
  HoneAbilitySubjects,
} from '@hone-automation/common';
import { useAuthContext } from '../../../../context/useAuthContext';
import { useQueueContext } from '../QueueContext';
import RichTextEditor, { RichTextEditorRef } from './RichTextEditor/RichTextEditor';
import { useAIQuery } from 'presentation/components/KAI/KAI.hook';
import { Tooltip, Whisper } from 'rsuite';

import AutoEnhancer from './AutoEnhancer';
import AttachmentsContainer from './Attachments/AttachmentContainer/AttachmentsContainer';
import useIsMobile from 'hooks/useIsMobile';
import { CMComposerContext, CMComposerContexts } from './types';
import UserSearch, { UserSearchType } from '../UserSearch/UserSearch';
import { useGetAllUsersByLocation } from 'hooks';
import { useMentionHandler } from './mentionUtils';
import { useComposerSubmit, useFileUpload, DestinationType } from './hooks';
import { SendButton } from './components';
import { useQueryState } from 'hooks/useQueryState';
import { useEvent } from '../../../../hooks/useEvent';
import { ProfileEntryParams } from 'presentation/components/NewTable/types';

export interface CMComposerRef {
  addFile: (file: File) => void;
  expand: () => void;
}

interface MessageComposerProps {
  /** The Ask request being responded to */
  request?: AskRequest | undefined;
  /** Initial content for editing mode */
  initialContent?: string;
  /** Flag indicating this is an edit operation */
  isEdit?: boolean;
  /** Callback when edit is saved */
  onSave?: (content: string) => void;
  /** Callback when edit is canceled */
  onCancel?: () => void;
  /** Handle OnCallback submit composer */
  handleOnCallback?: (
    formData: FormData,
    createRequestType: AskRequestCreateType
  ) => void | false | Promise<void | false>;
  /** Flag to send request */
  sendType?: AskRequestCreateType;
  /** Flag indicating this is a mobile device */
  mobile?: boolean;
  /** Flag to hide reopen label */
  hideReopenLabel?: boolean;
  /** Is Create new request mode */
  isCreateMode?: boolean;
  /** Flag to show message loading */
  messageLoading?: boolean;
  /** CMComposer context */
  context?: CMComposerContext;
  /** Override locationId (useful for multi-location flagging) */
  overrideLocationId?: string;
  /** Callback when message is being sent (for optimistic UI) */
  onMessageSending?: (content: string) => void;
  /** Callback when message has been successfully sent */
  onMessageSent?: (request: AskRequest) => void;
  /** External control for internal mode (used by create modals) */
  isInternal?: boolean;
  /** Custom label for the submit button (e.g., "Create", "Send") */
  isRightPanelComposer?: boolean;
  /** Validate is right side panel */
  submitLabel?: string;
  /** Auto-focus the editor when mounted */
  autoFocus?: boolean;
}
/**
 * Message composer component for creating and sending responses to customer requests.
 * Features rich text editing, emoji insertion, and optional AI assistance for message enhancement.
 *
 * @param {MessageComposerProps} props - The component props
 * @param {AskRequest} props.request - The Ask request being responded to
 * @param {boolean} [props.KAI=false] - Whether to enable KAI (AI assistance) features
 * @param {string} [props.initialContent] - Initial content for editing mode
 * @param {boolean} [props.isEdit] - Flag indicating this is an edit operation
 * @param {Function} [props.onSave] - Callback when edit is saved
 * @param {Function} [props.onCancel] - Callback when edit is canceled
 * @param {Function} [props.handleOnCallback] - Callback for custom submit action
 * @param {boolean} [props.hideReopenLabel] - Whether to hide the reopen label
 * @param {boolean} [props.messageLoading] - Whether the message is loading
 * @param {AskRequestCreateType} [props.sendType] - Type of request to send
 * @param {boolean} [props.context] - CMComposer context
 * 
 
 * @returns {JSX.Element} The rendered message composer
 */
const MessageComposer = forwardRef<CMComposerRef, MessageComposerProps>(
  (
    {
      request,
      initialContent = '',
      isEdit = false,
      onSave,
      onCancel,
      handleOnCallback,
      hideReopenLabel = false,
      messageLoading = false,
      isCreateMode = false,
      sendType,
      context,
      overrideLocationId,
      onMessageSending,
      onMessageSent,
      isRightPanelComposer = false,
      isInternal: isInternalProp,
      submitLabel,
      autoFocus: autoFocusProp = false,
    },
    ref
  ) => {
    const mobile = useIsMobile();
    const [status] = useQueryState('status');
    const [, setRequestId] = useQueryState('requestId');
    const { locationId: urlLocationId } = useParams();
    const effectiveLocationId = overrideLocationId || urlLocationId;
    const { currentLocationAbility } = useAuthContext();
    const { dispatch: emitEvent } = useEvent('onRequestToInProgress');
    const { dispatch: emitEventChangeStatus } = useEvent('onQueueRequestChangeStatus');

    const canManageQueue = currentLocationAbility.can(HoneAbilityActions.manage, HoneAbilitySubjects.Queue);
    const sendTypeFinal =
      sendType || (canManageQueue ? AskRequestCreateTypes.For_Customer : AskRequestCreateTypes.For_Accountant);

    const {
      dispatch,
      state: { selectedItem, requests },
    } = useQueueContext();
    const { locationId: routeLocationId } = useParams<ProfileEntryParams>();

    const { data: usersFetched } = useGetAllUsersByLocation(routeLocationId);
    const { user } = useAuthContext();

    const KAI = true;
    const selectedItemFinal = selectedItem || request;

    // Enhanced mention handling with our custom hook
    const richTextEditorRef = useRef<RichTextEditorRef>(null);

    // Define a type for our mention handler ref
    type MentionHandlerRefType = {
      setLastAtPosition: (position: number) => void;
    };

    const mentionHandlerRef = useRef<MentionHandlerRefType | null>(null);

    const {
      userSearchOpen,
      searchText,
      handleTextChange,
      setUserSearchOpen,
      handleUserSelect,
      handleKeyDown: handleMentionKeyDown,
      setLastAtPosition,
    } = useMentionHandler(richTextEditorRef, usersFetched);

    // Store the mention handler functions in the ref for access in other functions
    useEffect(() => {
      // Create a new object with the functions we need
      mentionHandlerRef.current = {
        setLastAtPosition,
      };
    }, [setLastAtPosition]);

    const [filteredUsers, setFilteredUsers] = useState<UserSearchType[]>([]);
    // Reset composer state when switching requests
    useEffect(() => {
      if (!request) return;

      setRichText(initialContent);
      setText(initialContent);
      setAttachments([]);
      setFile(null);
      setToolbar(false);
      setEmojiPicker(false);
      setIsExpanded(isEdit || !!initialContent);
      setDestinationDropdownOpen(false);
      setSelectedDestination('ALL');
      setUserSearchOpen(false);
      setKAIclicked(false);
      prevRequestIdRef.current = undefined;
    }, [request?.id, initialContent, isEdit]);

    const handleRequestReopen = (trx: AskRequest | undefined) => {
      if (trx && status === 'Closed') {
        //filter request from queue
        const newRequestsByStatus = requests
          .filter(req => req.id !== trx.id && req.status === AskRequestStatuses.Closed)
          .sort((a, b) => {
            return new Date(a.updatedAt || a.createdAt).getTime() - new Date(b.updatedAt || b.createdAt).getTime();
          })
          .reverse();
        const newRequests = [...new Set([...requests, ...newRequestsByStatus])].filter(req => req.id !== trx.id);
        //push old request to new state
        newRequests.push(trx);
        //update requests in context
        dispatch({ type: 'SET_REQUESTS', payload: newRequests });
        const nextTrx = newRequestsByStatus[0];
        dispatch({ type: 'SET_SELECTED_ITEM', payload: nextTrx as unknown as AskRequest });
        setRequestId(nextTrx?.id);
        emitEventChangeStatus({
          field: trx.status,
          duration: 3000,
          message: 'Request reopened successfully.',
        });
      }
    };

    const handleRequestSubmit = (trx: AskRequest | undefined) => {
      if (trx) {
        // filter request from queue
        const newRequestsByStatus = requests
          .filter(req => req.status === status && req.id !== trx.id)
          .sort((a, b) => {
            return new Date(a.updatedAt || a.createdAt).getTime() - new Date(b.updatedAt || b.createdAt).getTime();
          });
        // remove duplicates and filter out the current request
        const newRequests = [...new Set([...requests, ...newRequestsByStatus])].filter(req => req.id !== trx.id);
        // push the updated version of the current request
        newRequests.push(trx);
        // Update the requests in the context
        dispatch({ type: 'SET_REQUESTS', payload: newRequests });

        // Groups always stay selected (they appear in all views, status doesn't change view membership)
        // For regular requests, select next item if status changed (request moved to different view)
        const isGroup = trx.type === 'Group';
        const nextTrx = !isGroup && trx.status !== status ? newRequestsByStatus[0] : trx;
        dispatch({ type: 'SET_SELECTED_ITEM', payload: nextTrx as unknown as AskRequest });
        // Update the requestId in the URL
        setRequestId(nextTrx?.id);
        //validate animation depending on status
        if (trx.status === AskRequestStatuses.In_Progress && status === AskRequestStatuses.Open) {
          emitEvent({
            field: AskRequestStatuses.In_Progress,
            duration: 3000,
            message: 'Request marked as in progress.',
          });
        }
        // Notify parent that message was successfully sent
        onMessageSent?.(trx);
      }
    };

    const requestStatus = request ? request?.status : 'Open';
    const { mutate: createCommentMutation, isPending } = useCreateQueueCommentMutation(
      requestStatus,
      effectiveLocationId!,
      handleRequestSubmit
    );

    const postReopenMutation = useReopenQueueRequestMutation(
      requestStatus,
      effectiveLocationId!,
      undefined,
      handleRequestReopen
    );

    const [toolbarOpen, setToolbar] = useState<boolean>(false);
    const [emojiPickerOpen, setEmojiPicker] = useState<boolean>(false);
    const [richText, setRichText] = useState<string>(initialContent);
    const [text, setText] = useState<string>(initialContent);
    const [isExpanded, setIsExpanded] = useState<boolean>(isEdit || !!initialContent);
    const justSubmittedRef = useRef(false);

    // File upload hook
    const {
      attachments,
      fileInputRef,
      handleOpenUploadFile,
      handleFileChange,
      handleDeleteAttachment,
      addFile,
      setAttachments,
      setFile,
    } = useFileUpload();

    // Expose methods via ref for external control (e.g., drag-and-drop from parent)
    useImperativeHandle(
      ref,
      () => ({
        addFile: (file: File) => {
          addFile(file);
          setIsExpanded(true);
        },
        expand: () => setIsExpanded(true),
      }),
      [addFile]
    );

    const [KAIclicked, setKAIclicked] = useState<boolean>(false);
    const [selectedDestination, setSelectedDestination] = useState<DestinationType>('ALL');
    const [isComponentHovered, setIsComponentHovered] = useState<boolean>(false);
    const [destinationDropdownOpen, setDestinationDropdownOpen] = useState<boolean>(false);

    // Sync selectedDestination with external isInternal prop (for create modals)
    useEffect(() => {
      if (isInternalProp !== undefined) {
        setSelectedDestination(isInternalProp ? 'INTERNAL' : 'ALL');
      }
    }, [isInternalProp]);

    const emojiPickerRef = useRef<HTMLDivElement | null>(null);
    const { sendQuery } = useAIQuery();
    const isClosedAndReopen = selectedItemFinal?.status === 'Closed' && selectedItemFinal?.canReopen;

    const handleEmojiRTEClick = (emojiData: EmojiClickData) => {
      if (richTextEditorRef.current) {
        richTextEditorRef.current.insertText(emojiData.emoji);
      }
      setEmojiPicker(false);

      setTimeout(() => {
        if (richTextEditorRef.current) {
          richTextEditorRef.current.focus();
        }
      }, 10);
    };

    const toggleEmojiPick = () => {
      setEmojiPicker(prevState => !prevState);
    };

    useClickAway(emojiPickerRef, () => {
      setEmojiPicker(false);
    });

    const handleRewrite = (message: string) => {
      if (richTextEditorRef.current) {
        richTextEditorRef.current.KAI(message);
      }
    };

    const handleDestinationSelect = (destination: 'ALL' | 'INTERNAL' | 'EMAIL') => {
      setSelectedDestination(destination);
      setDestinationDropdownOpen(false);
    };

    // Determine the CSS mode class based on selected destination
    const composerModeClass = useMemo(() => {
      if (selectedDestination === 'INTERNAL') return 'int-mode';
      if (selectedDestination === 'EMAIL') return 'email-mode';
      return '';
    }, [selectedDestination]);

    const shouldShowInternal = useMemo(() => {
      if (requestStatus === AskRequestStatuses.Closed) return false;
      if (context === CMComposerContexts.createRequest) return sendTypeFinal !== AskRequestCreateTypes.For_Customer;
      if (context === CMComposerContexts.queue || context === CMComposerContexts.flaggedCell) return true;
      return false;
    }, [context, sendTypeFinal, requestStatus]);

    // Show email option when request type is Email and user can manage queue
    const shouldShowEmailOption = useMemo(() => {
      if (requestStatus === AskRequestStatuses.Closed) return false;
      if (!canManageQueue) return false;
      return request?.type === 'Email';
    }, [request?.type, canManageQueue, requestStatus]);

    // Check if all comments are internal
    const allCommentsAreInternal = useMemo(() => {
      if (!request?.comments?.length) return false;
      return request.comments.every(comment => comment.visibility === 'Internal');
    }, [request?.comments]);

    // Check if the last comment is internal
    const lastCommentIsInternal = useMemo(() => {
      if (!request?.comments?.length) return false;
      const lastComment = request.comments[request.comments.length - 1];
      return lastComment?.visibility === 'Internal';
    }, [request?.comments]);

    // If all comments are internal, force INTERNAL mode (no dropdown)
    // If only the last comment is internal and user is internal, default to INTERNAL but allow switching
    const forceInternalMode = allCommentsAreInternal && shouldShowInternal;
    // Groups only have simple "Send" - no destination options
    const isGroup = request?.type === 'Group';
    const hasDropdownOptions = forceInternalMode || isGroup ? false : shouldShowInternal || shouldShowEmailOption;

    // Use ref to always have latest isEdit value
    const isEditRef = useRef(isEdit);
    isEditRef.current = isEdit;

    // Reset fields callback for submission
    const resetFields = useCallback(() => {
      setRichText('');
      setText('');
      setFile(null);
      setAttachments([]);
      // Collapse composer after submission (unless in edit mode)
      if (!isEditRef.current) {
        setIsExpanded(false);
      }
    }, [setFile, setAttachments]);

    // Use the submission hook
    const { handleSubmit, handleSubmitWithDestination } = useComposerSubmit({
      text,
      richText,
      attachments,
      request,
      selectedItem,
      isClosedAndReopen: isClosedAndReopen ?? false,
      userId: user?.uid,
      locationId: effectiveLocationId,
      isEdit,
      sendTypeFinal,
      selectedDestination,
      shouldShowInternal,
      shouldShowEmailOption,
      onSave,
      handleOnCallback,
      createCommentMutation,
      postReopenMutation,
      resetFields,
      setKAIclicked,
      richTextEditorRef,
    });

    const handleSubmitAndCollapse = useCallback(() => {
      if (!text.trim() && attachments.length === 0) return;
      // Set flag to prevent handleFocus from re-expanding
      justSubmittedRef.current = true;
      // Notify parent that message is being sent (for optimistic UI)
      if (onMessageSending && !isEditRef.current) {
        onMessageSending(richText);
      }
      handleSubmitWithDestination();
      // Force collapse and reset hover state to prevent handleBlur from blocking
      if (!isEditRef.current) {
        setIsComponentHovered(false);
        setIsExpanded(false);
        // Blur the editor to remove focus and show placeholder
        richTextEditorRef.current?.blur();
      }
      // Reset flag after a short delay
      setTimeout(() => {
        justSubmittedRef.current = false;
      }, 300);
    }, [attachments.length, handleSubmitWithDestination, text, richText, onMessageSending]);

    const handleComponentMouseEnter = () => {
      setIsComponentHovered(true);
    };

    const handleComponentMouseLeave = () => {
      setIsComponentHovered(false);
    };

    /**
     * Enhanced rich text change handler using our mention utilities
     */
    const handleRichTextChange = (html: string, plain: string, mentions: string[] = []) => {
      // Use our enhanced mention handler
      handleTextChange(html, plain);

      setRichText(html);
      setText(plain);
    };

    const handleFocus = () => {
      // Don't re-expand if we just submitted
      if (justSubmittedRef.current) return;
      if (!isExpanded) {
        setIsExpanded(true);
      }
    };

    const handleBlur = () => {
      // Use a short delay to allow for mouse interactions
      setTimeout(() => {
        // Don't collapse if user is hovering over component
        if (isComponentHovered) {
          return;
        }

        // Check if focus moved to another element within the component
        const activeElement = document.activeElement;
        if (activeElement && activeElement.closest('.message-composer')) {
          return;
        }

        // Only collapse if there's no content and not in edit mode
        if (!isEdit && !text.trim() && !attachments.length) {
          setIsExpanded(false);
          setToolbar(!mobile);
        }
      }, 150);
    };

    const hideActions = false;

    const getFilteredUsers = (users: UserSearchType[]) => {
      setFilteredUsers(users);
    };

    /**
     * Handle paste events to detect and add images as attachments
     */
    const handlePaste = (e: React.ClipboardEvent) => {
      const items = e.clipboardData?.items;
      if (!items) return;

      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.type.startsWith('image/')) {
          e.preventDefault();
          const file = item.getAsFile();
          if (file) {
            // Generate a meaningful filename with timestamp
            const extension = item.type.split('/')[1] || 'png';
            const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
            const namedFile = new File([file], `pasted-image-${timestamp}.${extension}`, {
              type: file.type,
            });
            addFile(namedFile);
            // Expand composer if collapsed
            if (!isExpanded) {
              setIsExpanded(true);
            }
          }
          return; // Only handle first image
        }
      }
    };

    /**
     * Enhanced keyboard handler that incorporates mention handling
     */
    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
      // When user search is open, don't handle any keyboard events in the RichTextEditor
      // The UserSearch component's input will handle everything
      if (userSearchOpen) {
        // Prevent RichTextEditor from handling these keys
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter' || e.key === 'Escape') {
          // Don't handle in RichTextEditor, UserSearch input will handle it
          return;
        }
      }

      // Handle ESC key to collapse composer (only if no content and not in edit mode)
      if (e.key === 'Escape') {
        e.preventDefault();
        // Close any open pickers/dropdowns first
        setEmojiPicker(false);
        setDestinationDropdownOpen(false);

        // Collapse composer if empty and not in edit mode
        if (!isEdit && !text.trim() && !attachments.length) {
          setIsExpanded(false);
          setToolbar(!mobile);
          // Clear any residual HTML to ensure placeholder shows
          setRichText('');
          setText('');
          richTextEditorRef.current?.getEditor()?.blur();
        }
        return;
      }

      // First, let mention handler process the event (only when dropdown is not open)
      const mentionHandled = handleMentionKeyDown(e);

      if (mentionHandled) {
        return; // If mention handler processed the event, don't continue
      }

      // Handle submission with Shift+Enter (only if user search dropdown is not open)
      if (e.key === 'Enter' && e.shiftKey && !userSearchOpen) {
        e.preventDefault();
        handleSubmitAndCollapse();
        return;
      }
    };

    // Memoized RichTextEditor with enhanced mention support
    const MemoizedRichTextEditor = useMemo(
      () => (
        <RichTextEditor
          ref={richTextEditorRef}
          value={richText}
          onChange={handleRichTextChange}
          onKeyDown={handleKeyDown}
          onSubmit={() => handleSubmit(sendType!)}
          onFocus={handleFocus}
          onBlur={handleBlur}
          autoFocus={autoFocusProp}
          className={`message-input ${toolbarOpen ? 'tooltip-open' : ''}`}
          maxHeight="150px"
          toolbar={isExpanded ? toolbarOpen : false}
          isUserSearchOpen={userSearchOpen}
          users={usersFetched}
          isExpanded={isExpanded}
        />
      ),
      [
        richText,
        toolbarOpen,
        userSearchOpen,
        sendType,
        mobile,
        isExpanded,
        handleRichTextChange,
        handleKeyDown,
        handleSubmit,
        handleFocus,
        handleBlur,
        usersFetched,
      ]
    );

    const isValidText = text.length > 0;
    const isInternal = canManageQueue && !request;

    // Auto-set destination based on comment visibility rules (only on request change, not during editing)
    const requestId = request?.id;
    const prevRequestIdRef = useRef<string | undefined>(undefined);
    useEffect(() => {
      // Skip auto-set logic when in edit mode to prevent flickering
      if (isEdit) return;

      // Only auto-set on request change, not on every selectedDestination change
      const isNewRequest = prevRequestIdRef.current !== requestId;
      prevRequestIdRef.current = requestId;

      // If all comments are internal, force INTERNAL mode (always enforce this)
      if (forceInternalMode && selectedDestination !== 'INTERNAL') {
        setSelectedDestination('INTERNAL');
        return;
      }

      // If last comment is internal and user can manage queue, default to INTERNAL
      // But only on initial load/request change - allow user to switch afterwards
      if (isNewRequest && lastCommentIsInternal && canManageQueue) {
        setSelectedDestination('INTERNAL');
        return;
      }
    }, [requestId, forceInternalMode, lastCommentIsInternal, canManageQueue, isEdit, selectedDestination]);

    // Separate effect for resetting invalid selections
    // Skip reset if isInternalProp is explicitly set to true (controlled from parent)
    useEffect(() => {
      if (selectedDestination === 'INTERNAL' && !shouldShowInternal && !isInternalProp) {
        setSelectedDestination('ALL');
      }
      if (selectedDestination === 'EMAIL' && !shouldShowEmailOption) {
        setSelectedDestination('ALL');
      }
    }, [shouldShowInternal, shouldShowEmailOption, selectedDestination, isInternalProp]);

    const shouldShowSendReopen = useMemo(() => {
      if (requestStatus === AskRequestStatuses.Closed) return true;
      return !allCommentsAreInternal;
    }, [requestStatus, allCommentsAreInternal]);

    const handleOpenUserSearch = () => {
      // Open the user search
      setUserSearchOpen(prevValue => !prevValue);

      // Reset lastAtPosition to indicate this is from the button click
      // The handleUserSelect function will detect this and use the current cursor position
      mentionHandlerRef.current?.setLastAtPosition(-1);

      // Focus the editor to ensure cursor position is available
      setTimeout(() => {
        if (richTextEditorRef.current) {
          richTextEditorRef.current.getEditor()?.focus();
        }
      }, 0);
    };

    const handleComposerClick = (e: React.MouseEvent) => {
      // Only handle click if collapsed and clicking on the composer area (not buttons)
      if (!isExpanded && !(e.target as HTMLElement).closest('button')) {
        setIsExpanded(true);
        setTimeout(() => {
          richTextEditorRef.current?.focus();
        }, 0);
      }
    };

    return (
      <div
        className={classNames('message-composer', composerModeClass, {
          'kai-active': KAIclicked,
          collapsed: !isExpanded,
        })}
        onMouseEnter={handleComponentMouseEnter}
        onMouseLeave={handleComponentMouseLeave}
        onClick={handleComposerClick}
        onPaste={handlePaste}
      >
        <UserSearch
          users={usersFetched}
          onUserSelect={handleUserSelect}
          onClose={() => setUserSearchOpen(false)}
          isOpen={userSearchOpen}
          searchText={searchText}
          getFilteredUsers={getFilteredUsers}
        />
        {KAI && isExpanded && (
          <AutoEnhancer
            message={text}
            rewriteMessageCallBack={handleRewrite}
            toolbarOpen={toolbarOpen}
            onKAIopen={open => setKAIclicked(open)}
          />
        )}
        <div className="message-row">
          {MemoizedRichTextEditor}
          {!isExpanded && (
            <button
              className={classNames('collapsed-send-button', {
                disabled: !isValidText,
              })}
              onClick={handleSubmitWithDestination}
              disabled={isPending || messageLoading || !isValidText}
            >
              <IconDirUp />
            </button>
          )}
        </div>
        <input
          id="file-upload"
          type="file"
          accept="*"
          className="hidden"
          onChange={handleFileChange}
          ref={fileInputRef}
        />
        {!!attachments.length && <AttachmentsContainer attachments={attachments} removeFile={handleDeleteAttachment} />}

        <div className="message-actions">
          <div className="left-actions">
            {!hideActions && isExpanded && (
              <>
                <div className="action-buttons">
                  <button
                    className={classNames('action-button', { active: toolbarOpen })}
                    onClick={() => setToolbar(prevValue => !prevValue)}
                  >
                    <span>Aa</span>
                  </button>
                  <button onClick={handleOpenUploadFile} className={classNames('action-button')}>
                    <div className="action-button-icon">
                      <IconAttachment />
                    </div>
                  </button>
                  {/* Hide emoji picker on mobile - keyboard has built-in emoji support */}
                  {!mobile && (
                    <button
                      onClick={toggleEmojiPick}
                      className={classNames('action-button', { active: emojiPickerOpen })}
                    >
                      <div className="action-button-icon">
                        <IconEmoji />
                      </div>
                    </button>
                  )}
                  <button
                    onClick={handleOpenUserSearch}
                    className={classNames('action-button', { active: userSearchOpen })}
                  >
                    <div className="action-button-icon">
                      <IconMention />
                    </div>
                  </button>

                  <div ref={emojiPickerRef} className="emoji-wrapper">
                    <EmojiPicker open={emojiPickerOpen} onEmojiClick={handleEmojiRTEClick} />
                  </div>
                </div>
                {!mobile && isRightPanelComposer && (
                  <div className={classNames('help', { 'help-visible': isValidText })}>
                    Use &nbsp; <span>Shift + Return</span> &nbsp; to send the message
                  </div>
                )}
              </>
            )}
          </div>
          {!mobile && !isRightPanelComposer && (
            <div className={classNames('help', { 'help-visible': isValidText })}>
              Use &nbsp; <span>Shift + Return</span> &nbsp; to send the message
            </div>
          )}
          <div className="right-actions">
            {KAI && (
              <Whisper
                placement="top"
                trigger="hover"
                delayOpen={1200}
                speaker={<Tooltip>Automatically enhance the message when it's sent</Tooltip>}
              >
                {null}
              </Whisper>
            )}
            {isEdit ? (
              <>
                <button className="message-cancel" onClick={onCancel}>
                  Cancel
                </button>
                <button
                  className={classNames('message-submit', {
                    disabled: !isValidText,
                    'message-save': true,
                  })}
                  onClick={() => handleSubmit(sendTypeFinal)}
                >
                  Save
                </button>
              </>
            ) : (
              <SendButton
                selectedDestination={selectedDestination}
                onDestinationSelect={handleDestinationSelect}
                destinationDropdownOpen={destinationDropdownOpen}
                onDropdownOpenChange={setDestinationDropdownOpen}
                canManageQueue={canManageQueue}
                hasDropdownOptions={hasDropdownOptions}
                shouldShowInternal={shouldShowInternal}
                shouldShowEmailOption={shouldShowEmailOption}
                isClosedAndReopen={isClosedAndReopen ?? false}
                hideReopenLabel={hideReopenLabel}
                isValidText={isValidText}
                isPending={isPending}
                messageLoading={messageLoading}
                composerModeClass={composerModeClass}
                onSubmit={handleSubmitAndCollapse}
                isCreateMode={isCreateMode}
                submitLabel={submitLabel}
              />
            )}
          </div>
        </div>
      </div>
    );
  }
);

MessageComposer.displayName = 'MessageComposer';

export default MessageComposer;
